/**
 * The server's evidence verdict, mapped back onto the form the CPA is looking at.
 *
 * ATTEST-EVIDENCE-REQUIRED-1 item 3. Two paths arrive here and they are not
 * symmetrical:
 *
 * - **The letter built.** `LetterArtifact.evidence_index` is one entry per
 *   statement, in the order they were sent, each carrying `match_status`. The
 *   mapping back to form rows is positional, and the position is decided by the
 *   same filter `buildLetterRequest` uses — which is why that predicate is
 *   duplicated below rather than eyeballed.
 * - **The letter was refused** with `ATTEST_EVIDENCE_NOT_VERIFIED`. There is no
 *   index: nothing was built. The findings carry `assertion_id` and the
 *   `reference` of the file that did not line up, so rows are matched on those.
 *
 * Nothing here decides anything. Every verdict is read off the response, and
 * every sentence shown to the CPA is the server's own `reason`. The client
 * cannot check entity, period or figure — the documents never leave the
 * backend — so a locally-computed verdict would be a guess wearing the engine's
 * authority.
 */
import type { AttestRefusal, GateFinding, LetterArtifact, MatchStatus } from '@/components/attest/api'
import type { StatementDraft } from '@/components/attest/formState'

/** Verified, contradicted, or "a human has to look at this". */
export type VerificationState = 'verified' | 'failed' | 'review'

export interface StatementVerification {
  state: VerificationState
  /** `VERIFIED`, or the code for the worst thing that did not line up. */
  code: MatchStatus
  /** The server's findings for this statement, prose included. */
  findings: GateFinding[]
}

/** Form statement index → verdict. Absent key means "not judged". */
export type VerificationMap = Map<number, StatementVerification>

/**
 * Which form statements were actually sent, in send order.
 *
 * Must stay identical to the filter in `buildLetterRequest`. If the two drift,
 * `evidence_index[3]` gets rendered against the wrong row — a verified document
 * shown as a mismatch, or worse, the reverse.
 */
function sentIndexes(statements: StatementDraft[]): number[] {
  return statements
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.assertion_id.trim() || s.text.trim())
    .map(({ i }) => i)
}

function stateFor(findings: GateFinding[]): VerificationState {
  if (findings.some(f => f.outcome === 'block')) return 'failed'
  if (findings.some(f => f.outcome === 'review')) return 'review'
  return 'verified'
}

/** Blocking codes win over review codes — same precedence as `GateReport`. */
function codeFor(findings: GateFinding[]): MatchStatus {
  const blocking = findings.find(f => f.outcome === 'block' && f.code)
  if (blocking) return blocking.code
  const review = findings.find(f => f.outcome === 'review' && f.code)
  return review ? review.code : 'VERIFIED'
}

function fromArtifact(
  statements: StatementDraft[],
  artifact: LetterArtifact,
): VerificationMap | null {
  const order = sentIndexes(statements)
  const index = artifact.evidence_index ?? []
  // A length mismatch means the artifact is not describing this form — an edit
  // landed between the build and this render, or an older build is still in
  // state. Showing nothing is right; guessing the alignment is not.
  if (index.length !== order.length) return null

  const map: VerificationMap = new Map()
  order.forEach((formIndex, i) => {
    const entry = index[i]
    if (!entry) return
    const findings = entry.review_findings ?? []
    map.set(formIndex, {
      // The artifact only exists because nothing blocked, so `verification`
      // here is `pass` or `review` and never `failed`.
      state: entry.verification === 'review' ? 'review' : 'verified',
      code: entry.match_status ?? 'VERIFIED',
      findings,
    })
  })
  return map
}

function referencesOf(statement: StatementDraft): string[] {
  return statement.evidence.map(e => e.reference.trim()).filter(Boolean)
}

function fromRefusal(
  statements: StatementDraft[],
  refusal: AttestRefusal,
): VerificationMap | null {
  if (refusal.error_code !== 'ATTEST_EVIDENCE_NOT_VERIFIED') return null
  const detail = refusal.detail as {
    evidence_findings?: GateFinding[]
    needs_review?: GateFinding[]
  }
  const all = [...(detail.evidence_findings ?? []), ...(detail.needs_review ?? [])]
  if (!all.length) return null

  const map: VerificationMap = new Map()
  sentIndexes(statements).forEach(formIndex => {
    const draft = statements[formIndex]
    const assertionId = draft.assertion_id.trim()
    const sameAssertion = all.filter(f => f.assertion_id === assertionId)
    if (!assertionId) return

    // One row per assertion is the common case, and then the assertion id is
    // the whole answer. Where a letter repeats an assertion, narrow by the file
    // the finding names — `includes` rather than equality because the pooled
    // figure check reports every document it drew on as one comma-joined
    // reference.
    const repeated = statements.filter(s => s.assertion_id.trim() === assertionId).length > 1
    const refs = referencesOf(draft)
    const findings = repeated
      ? sameAssertion.filter(f => !f.reference || refs.some(r => f.reference.includes(r)))
      : sameAssertion

    map.set(formIndex, {
      // Silence is a pass here too: the gates ran over every statement before
      // raising, so a row with no finding is a row whose documents matched.
      // Saying so is the point — it tells the CPA which one to fix.
      state: stateFor(findings),
      code: codeFor(findings),
      findings,
    })
  })
  return map
}

/**
 * The verdict for each form statement, or `null` when the server has not judged
 * this form yet — no build, or a build whose result no longer describes it.
 *
 * `null` is not a neutral default the UI can fill in: it means the checklist
 * goes back to saying "attached", which is the honest word for a document
 * nobody has checked.
 */
export function readVerification(
  statements: StatementDraft[],
  artifact: LetterArtifact | null,
  refusal: AttestRefusal | null,
): VerificationMap | null {
  if (artifact) return fromArtifact(statements, artifact)
  if (refusal) return fromRefusal(statements, refusal)
  return null
}
